import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Maximize, Pause, Play, SkipBack, SkipForward, StepBack, StepForward } from 'lucide-react'
import { useEditor } from '../store/editor'
import { player } from '../engine/player'
import { ASPECTS, aspectKey, confirmAspectChange, formatTime } from '../lib/model'
import { stepFrames, togglePlay } from '../lib/shortcuts'

export default function Preview() {
  const stageRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const W = useEditor((s) => s.project.settings.width)
  const H = useEditor((s) => s.project.settings.height)
  const [box, setBox] = useState({ w: 0, h: 0 })

  useEffect(() => {
    if (canvasRef.current) player.attach(canvasRef.current)
    return () => player.detach()
  }, [])

  useLayoutEffect(() => {
    const el = stageRef.current
    if (!el) return
    const fit = () => {
      const pad = 24
      const aw = el.clientWidth - pad * 2
      const ah = el.clientHeight - pad * 2
      const k = Math.min(aw / W, ah / H)
      setBox({ w: Math.max(10, Math.floor(W * k)), h: Math.max(10, Math.floor(H * k)) })
    }
    fit()
    const ro = new ResizeObserver(fit)
    ro.observe(el)
    return () => ro.disconnect()
  }, [W, H])

  return (
    <div className="center">
      <div className="stage" ref={stageRef} onPointerDown={(e) => { if (e.target === e.currentTarget) useEditor.getState().select([]) }}>
        <div className="stage-inner" style={{ width: box.w, height: box.h }}
          onPointerDown={(e) => { if (e.target === canvasRef.current) pickAt(e, box.w / W) }}>
          <canvas ref={canvasRef} />
          <SelectionBox k={box.w / W} />
          <EmptyHint />
        </div>
      </div>
      <Transport stage={stageRef} />
    </div>
  )
}

function EmptyHint() {
  const empty = useEditor((s) => s.project.clips.length === 0)
  if (!empty) return null
  return (
    <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: 'var(--fg-dim)', textAlign: 'center', padding: 20, pointerEvents: 'none' }}>
      <div>
        <div style={{ fontSize: 15, color: 'var(--fg-muted)', marginBottom: 6 }}>Empieza añadiendo un vídeo</div>
        <div>Importa archivos, abre un vídeo de Vaideo o añade un texto.</div>
      </div>
    </div>
  )
}

/** Click on the canvas selects the top-most clip under the pointer. */
function pickAt(e: React.PointerEvent, k: number) {
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
  const x = (e.clientX - rect.left) / k
  const y = (e.clientY - rect.top) / k
  const s = useEditor.getState()
  const p = s.project
  const order = new Map(p.tracks.map((t, i) => [t.id, i]))
  const hits = [...player.bounds.entries()].filter(([, b]) => {
    const r = (-b.rotation * Math.PI) / 180
    const dx = x - b.cx
    const dy = y - b.cy
    const lx = dx * Math.cos(r) - dy * Math.sin(r)
    const ly = dx * Math.sin(r) + dy * Math.cos(r)
    return Math.abs(lx) <= b.w / 2 && Math.abs(ly) <= b.h / 2
  })
  if (!hits.length) { s.select([]); return }
  const clips = hits.map(([id]) => p.clips.find((c) => c.id === id)!).filter(Boolean)
  // texts are drawn above media; then front-most track (lowest index)
  clips.sort((a, b) => {
    const ta = a.kind === 'text' || a.kind === 'subtitle' ? 0 : 1
    const tb = b.kind === 'text' || b.kind === 'subtitle' ? 0 : 1
    return ta - tb || (order.get(a.trackId)! - order.get(b.trackId)!)
  })
  const pick = clips[0]
  s.select([pick.id])
  // start dragging immediately
  startDrag(e, pick.id, 'move', k)
}

type Mode = 'move' | 'scale' | 'rotate'

function startDrag(e: React.PointerEvent, clipId: string, mode: Mode, k: number) {
  e.preventDefault()
  e.stopPropagation()
  const s = useEditor.getState()
  const c0 = s.project.clips.find((c) => c.id === clipId)
  const b0 = player.bounds.get(clipId)
  if (!c0 || !b0) return
  const track = s.project.tracks.find((t) => t.id === c0.trackId)
  if (track?.locked) return
  const x0 = e.clientX
  const y0 = e.clientY
  const t0 = { ...c0.transform }
  const stageEl = (e.currentTarget as HTMLElement).closest('.stage-inner') as HTMLElement
  const rect = stageEl.getBoundingClientRect()
  const cxScreen = rect.left + b0.cx * k
  const cyScreen = rect.top + b0.cy * k
  const d0 = Math.hypot(x0 - cxScreen, y0 - cyScreen) || 1
  const a0 = Math.atan2(y0 - cyScreen, x0 - cxScreen)
  let started = false
  const W = s.project.settings.width
  const H = s.project.settings.height
  const guides = document.createElement('div')
  stageEl.appendChild(guides)

  const move = (ev: PointerEvent) => {
    const dx = (ev.clientX - x0) / k
    const dy = (ev.clientY - y0) / k
    if (!started) {
      if (Math.hypot(dx * k, dy * k) < 3) return
      started = true
      useEditor.getState().beginGesture()
    }
    const patch: Partial<typeof t0> = {}
    guides.innerHTML = ''
    if (mode === 'move') {
      let nx = t0.x + dx
      let ny = t0.y + dy
      const snap = 10 / k
      const isSubPreset = c0.kind === 'subtitle' && c0.positionMode !== 'free'
      if (Math.abs(nx) < snap) { nx = 0; guides.innerHTML += `<div class="guide" style="left:${(W / 2) * k - 0.5}px;top:0;bottom:0;width:1px"></div>` }
      if (!isSubPreset && Math.abs(ny) < snap) { ny = 0; guides.innerHTML += `<div class="guide" style="top:${(H / 2) * k - 0.5}px;left:0;right:0;height:1px"></div>` }
      patch.x = Math.round(nx)
      patch.y = isSubPreset ? t0.y : Math.round(ny)
    } else if (mode === 'scale') {
      const d = Math.hypot(ev.clientX - cxScreen, ev.clientY - cyScreen)
      patch.scale = Math.max(0.05, Math.min(10, t0.scale * (d / d0)))
    } else {
      const a = Math.atan2(ev.clientY - cyScreen, ev.clientX - cxScreen)
      let deg = t0.rotation + ((a - a0) * 180) / Math.PI
      deg = ((deg + 540) % 360) - 180
      for (const snapDeg of [-180, -90, 0, 90, 180]) if (Math.abs(deg - snapDeg) < 4) deg = snapDeg
      patch.rotation = Math.round(deg * 10) / 10
    }
    useEditor.getState().mutate((p) => {
      const c = p.clips.find((x) => x.id === clipId)
      if (c) c.transform = { ...c.transform, ...patch }
    }, { history: false })
  }
  const up = () => {
    guides.remove()
    window.removeEventListener('pointermove', move)
    window.removeEventListener('pointerup', up)
  }
  window.addEventListener('pointermove', move)
  window.addEventListener('pointerup', up)
}

function SelectionBox({ k }: { k: number }) {
  const ref = useRef<HTMLDivElement>(null)
  const sel = useEditor((s) => (s.selection.length === 1 ? s.selection[0] : null))
  const kind = useEditor((s) => s.project.clips.find((c) => c.id === sel)?.kind)

  useEffect(() => {
    const update = () => {
      const el = ref.current
      if (!el) return
      const b = sel ? player.bounds.get(sel) : undefined
      if (!b) { el.style.display = 'none'; return }
      el.style.display = 'block'
      el.style.left = `${(b.cx - b.w / 2) * k}px`
      el.style.top = `${(b.cy - b.h / 2) * k}px`
      el.style.width = `${b.w * k}px`
      el.style.height = `${b.h * k}px`
      el.style.transform = b.rotation ? `rotate(${b.rotation}deg)` : ''
    }
    update()
    return player.onDraw(update)
  }, [sel, k])

  if (!sel) return null
  const rotatable = kind === 'video' || kind === 'image'
  return (
    <div ref={ref} className="sel-box" style={{ display: 'none' }} onPointerDown={(e) => startDrag(e, sel, 'move', k)}>
      {(['nw', 'ne', 'sw', 'se'] as const).map((h) => (
        <div key={h} className={`h ${h}`} onPointerDown={(e) => startDrag(e, sel, 'scale', k)} />
      ))}
      {rotatable && <div className="rot" title="Girar" onPointerDown={(e) => startDrag(e, sel, 'rotate', k)} />}
    </div>
  )
}

function Transport({ stage }: { stage: React.RefObject<HTMLDivElement> }) {
  const playing = useEditor((s) => s.playing)
  const t = useEditor((s) => s.playhead)
  const dur = useEditor((s) => s.project.durationSec)
  const fps = useEditor((s) => s.project.settings.fps)
  const W = useEditor((s) => s.project.settings.width)
  const H = useEditor((s) => s.project.settings.height)
  const key = aspectKey(W, H)
  const s = useEditor.getState
  return (
    <div className="transport">
      <div className="timecode"><b>{formatTime(t, fps)}</b> / {formatTime(dur, fps)}</div>
      <div className="spacer" />
      <button className="btn ghost icon" title="Inicio (Inicio)" onClick={() => { s().set({ playing: false }); s().setPlayhead(0) }}><SkipBack size={16} /></button>
      <button className="btn ghost icon" title="Fotograma anterior (←)" onClick={() => stepFrames(-1)}><StepBack size={16} /></button>
      <button className="play" title="Reproducir / pausar (Espacio)" onClick={togglePlay}>
        {playing ? <Pause size={17} fill="#fff" /> : <Play size={17} fill="#fff" style={{ marginLeft: 2 }} />}
      </button>
      <button className="btn ghost icon" title="Fotograma siguiente (→)" onClick={() => stepFrames(1)}><StepForward size={16} /></button>
      <button className="btn ghost icon" title="Final (Fin)" onClick={() => { s().set({ playing: false }); s().setPlayhead(dur) }}><SkipForward size={16} /></button>
      <div className="spacer" />
      <select className="aspect-select" value={ASPECTS.some((a) => a.key === key) ? key : 'custom'} title="Formato del lienzo"
        onChange={(e) => {
          const a = ASPECTS.find((x) => x.key === e.target.value)
          if (a && confirmAspectChange(s().project, a.width, a.height)) {
            s().mutate((p) => { p.settings.width = a.width; p.settings.height = a.height })
          }
        }}>
        {ASPECTS.map((a) => <option key={a.key} value={a.key}>{a.label} · {a.hint}</option>)}
        {!ASPECTS.some((a) => a.key === key) && <option value="custom">{W}×{H}</option>}
      </select>
      <button className="btn ghost icon" title="Pantalla completa" onClick={() => stage.current?.requestFullscreen?.()}><Maximize size={16} /></button>
    </div>
  )
}
